import { InfoIcon } from 'lucide-react';
import { CloudIcon } from 'lucide-react';

import { Loader } from '@/components/Loader';
import { Tooltip } from '@/components/Tooltip';
import { Container } from '@/components/ui';
import { MonitoringCounts, SubscriptionConfiguration } from '@/types';
import { cn } from '@/utils/classname';
import { calculateCloudProviderCount } from '@/utils/monitoring/transformers';

import { NotIncludedBadge } from './NotIncludedBadge';

interface CloudCardProps {
  monitoringCounts: MonitoringCounts;
  subscriptionValue?: SubscriptionConfiguration;
  isLoading: boolean;
}

const cloudProviders: Array<{
  key: keyof MonitoringCounts['cloud'];
  label: string;
}> = [
  { key: 'amazon', label: 'AWS' },
  { key: 'azure', label: 'Azure' },
  { key: 'gcp', label: 'GCP' },
  { key: 'digitalocean', label: 'Digital Ocean' },
];

export function CloudCard({
  monitoringCounts,
  subscriptionValue,
  isLoading,
}: CloudCardProps) {
  const attackSurface = subscriptionValue?.attackSurface;

  const getCloudProviderTooltip = (providerLabel: string) => {
    if (attackSurface?.cloud) {
      return `External and internal assets from ${providerLabel} cloud infrastructure`;
    }
    if (attackSurface?.external) {
      return `External assets from ${providerLabel} cloud infrastructure`;
    }
    return '';
  };

  const cloudCovered = Boolean(attackSurface?.cloud || attackSurface?.external);

  const cloudTotal = cloudProviders.reduce(
    (sum, { key }) =>
      sum +
      calculateCloudProviderCount(monitoringCounts.cloud[key], {
        cloud: Boolean(attackSurface?.cloud),
        external: Boolean(attackSurface?.external),
      }),
    0
  );

  return (
    <Container className="flex h-full w-full flex-col gap-3">
      <div className="mb-1 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-bold">Cloud</h3>
        </div>
        <div className="flex items-center gap-2">
          {!cloudCovered && <NotIncludedBadge />}
          <Loader
            className="my-[5.5px] h-[16px] w-[48px] rounded-3xl"
            isLoading={isLoading}
          >
            <div className="border-chariot-stroke text-chariot-text-primary rounded-2xl border px-3 py-1 text-xs font-medium">
              now
            </div>
          </Loader>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <CloudIcon className="text-chariot-stroke size-6 shrink-0 [&_path]:stroke-[2px]" />
        <h4 className="text-sm font-bold whitespace-break-spaces">
          Total Number of Cloud Assets Monitored
        </h4>
        <Loader
          className="my-[5.5px] ml-auto h-[16px] w-[48px] rounded-3xl"
          isLoading={isLoading}
        >
          <div className="bg-chariot-background-secondary ml-auto min-w-12 rounded-md px-3 py-1 text-center text-sm font-bold">
            {cloudTotal.toString()}
          </div>
        </Loader>
      </div>
      <div className={cn('bg-chariot-stroke h-px w-full')} />
      <div className="flex flex-col gap-4">
        {cloudProviders.map(({ key, label }) => {
          const count = calculateCloudProviderCount(
            monitoringCounts.cloud[key],
            {
              cloud: Boolean(attackSurface?.cloud),
              external: Boolean(attackSurface?.external),
            }
          );
          const tooltip = getCloudProviderTooltip(label);
          return (
            <div className="flex w-full items-center justify-between" key={key}>
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-bold whitespace-break-spaces capitalize">
                  {label}
                </h4>
                {tooltip && (
                  <Tooltip title={tooltip}>
                    <InfoIcon className="text-chariot-text-primary size-4" />
                  </Tooltip>
                )}
              </div>
              <Loader
                className="my-[5.5px] ml-auto h-[16px] w-[48px] rounded-3xl"
                isLoading={isLoading}
              >
                <div className="bg-chariot-background-secondary min-w-12 rounded-md px-3 py-1 text-center text-sm font-bold">
                  {count.toString()}
                </div>
              </Loader>
            </div>
          );
        })}
      </div>
    </Container>
  );
}
